/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Grid from '@/config/Grid/Grid'
import Tile from '@/shared/components/Tile/Tile'
import Table from '@/shared/components/Table/Table'
import Button from '@/shared/components/Button/Button'
import CheckBox from '@/shared/components/CheckBox/CheckBox'
import Chip from '@/shared/components/Chip/Chip'
import Modal from '@/shared/components/Modal/Modal'
import ConfirmationModal from '@/shared/components/Modal/ConfirmationModal/ConfirmationModal'
import { useDiscardConfirmation } from '@/shared/components/Modal/ConfirmationModal/useDiscardConfirmation'
import DynamicForm from '@/shared/components/DynamicForm/DynamicForm'
import { TABLE_KEYS } from '@/shared/components/Table/models/Filter.model'
import { OptionType } from '@/shared/api/BaseAPI.model'
import {
  DEFAULT_MAX_PER_REDEMPTION,
  LoyaltyRules,
  LoyaltyStats,
  POINTS_VALIDITY_MONTHS,
  useLoyaltyQuery,
  useLoyaltyStore,
  useRewardColumnsConfig,
  useRewardDishOptions,
  useRewardFormConfig
} from '@/features/loyalty'
import styles from '@/features/loyalty/LoyaltyPage.module.scss'

const columnsModel = ['dishes', 'points', 'isActive', 'actions']

function Loyalty() {
  const { t, i18n } = useTranslation()

  const isActive = useLoyaltyStore(s => s.isActive)
  const rules = useLoyaltyStore(s => s.rules)
  const rewards = useLoyaltyStore(s => s.rewards)
  const stats = useLoyaltyStore(s => s.stats)
  const setIsActive = useLoyaltyStore(s => s.setIsActive)
  const setRules = useLoyaltyStore(s => s.setRules)
  const addReward = useLoyaltyStore(s => s.addReward)
  const updateReward = useLoyaltyStore(s => s.updateReward)
  const deleteRewards = useLoyaltyStore(s => s.deleteRewards)

  useLoyaltyQuery()

  /*
    The options tile is draft-then-commit rather than write-on-change, because it
    now has a save button and a button that saves what is already saved is a lie.
    Re-seeded whenever the stored values change under it, which is what hydration
    from the API will look like once there is one.
  */
  const [draftIsActive, setDraftIsActive] = useState(isActive)
  const [draftRules, setDraftRules] = useState(rules)

  useEffect(() => {
    setDraftIsActive(isActive)
  }, [isActive])

  useEffect(() => {
    setDraftRules(rules)
  }, [rules])

  const hasUnsavedOptions =
    draftIsActive !== isActive ||
    draftRules.pointsPerMajorUnit !== rules.pointsPerMajorUnit ||
    draftRules.minimumBillMinor !== rules.minimumBillMinor ||
    draftRules.contactBonusPoints !== rules.contactBonusPoints

  const handleSaveOptions = () => {
    setIsActive(draftIsActive)
    setRules(draftRules)
  }

  const [editedRewardId, setEditedRewardId] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  /*
    Rewards waiting for the bin to be confirmed — one id from a row, several from
    the toolbar. Null means nothing is pending, which is not the same as an empty
    array, and the array is what tells the two callers apart.
  */
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[] | null>(null)

  const baseRewardFormConfig = useRewardFormConfig()
  const { dishOptions } = useRewardDishOptions()

  const [isRewardFormDirty, setIsRewardFormDirty] = useState(false)

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditedRewardId(null)
    setIsRewardFormDirty(false)
  }

  const {
    requestClose: requestCloseModal,
    discardConfirmation: rewardDiscardConfirmation
  } = useDiscardConfirmation(isRewardFormDirty, handleCloseModal)

  const handleAddReward = () => {
    setEditedRewardId(null)
    setIsModalOpen(true)
  }

  const handleEditReward = (id: string) => {
    setEditedRewardId(id)
    setIsModalOpen(true)
  }

  const handleDeleteReward = (id: string) => setPendingDeleteIds([id])

  const handleConfirmDelete = () => {
    if (pendingDeleteIds) deleteRewards(pendingDeleteIds)
    setPendingDeleteIds(null)
  }

  /*
    Whether a reward is offered is not part of the form: it is one click in the
    row's actions, so a manager can pull a reward for a week without reopening
    the modal and submitting it again.
  */
  const handleToggleRewardActive = (id: string) => {
    const reward = rewards.find(item => item.id === id)
    if (reward) updateReward(id, { isActive: !reward.isActive })
  }

  const columns = useRewardColumnsConfig(
    handleEditReward,
    handleDeleteReward,
    handleToggleRewardActive
  )

  /*
    The grid needs something it can sort and filter on, and the reward stores ids
    only. Names are resolved here, once, rather than in a cell renderer that
    would look them up per row on every render.
  */
  const rewardRows = useMemo(
    () =>
      rewards.map(reward => ({
        ...reward,
        dishes: reward.dishIds
          .map((id: string) => dishOptions.find(option => option.value === id))
          .filter((option): option is OptionType => !!option)
          .map(option => ({ id: option.value, label: option.label as string }))
      })),
    [rewards, dishOptions]
  )

  const rewardFormConfig = useMemo(() => {
    const edited = rewards.find(reward => reward.id === editedRewardId)

    return {
      ...baseRewardFormConfig,
      /*
        Spelled out for the add case too, rather than left undefined: spreading
        the base config alone would leave the previously edited reward's values
        in the form, and the empty strings are what DynamicForm would otherwise
        build for these fields itself. The limit is the one field that starts on
        a value — one reward at a time is what almost every reward means.

        Ids mapped back to options rather than passed raw: the multi-select
        speaks `{ label, value }`, and an id whose dish has since been deleted
        resolves to nothing and is dropped instead of becoming a blank chip.
      */
      defaultValues: edited
        ? {
            dishes: edited.dishIds
              .map((id: string) =>
                dishOptions.find(option => option.value === id)
              )
              .filter((option): option is OptionType => !!option),
            points: edited.points,
            maxPerRedemption: edited.maxPerRedemption
          }
        : {
            dishes: [],
            points: '',
            maxPerRedemption: DEFAULT_MAX_PER_REDEMPTION
          },
      onSubmit: (data: any) => {
        const payload = {
          dishIds: ((data.dishes ?? []) as OptionType[]).map(
            option => option.value as string
          ),
          points: Number(data.points),
          maxPerRedemption: Number(data.maxPerRedemption)
        }
        // Editing leaves `isActive` alone — the eye in the row owns it. A new
        // reward starts offered; adding one that is hidden makes no sense.
        if (editedRewardId) updateReward(editedRewardId, payload)
        else addReward({ ...payload, isActive: true })
        handleCloseModal()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRewardFormConfig, editedRewardId, rewards, dishOptions])

  return (
    <Grid>
      {/*
        Wide left column for what there is to read — the programme, its numbers
        and its rewards — and a narrow right one for the controls that set it,
        the same split the menu tab uses between its content and its settings.
      */}
      <div className={`${styles.loyaltyColumn} start-2 col-3`}>
        <Tile color="black">
          <div className={styles.loyaltySection}>
            <p className={`title-bold-medium ${styles.loyaltyTitle}`}>
              {t('LOYALTY.RULES.TITLE')}
            </p>
            {/*
              Both notes are rendered here rather than one here and one inside
              LoyaltyRules, so the numbering has a single owner. Validity is not
              a setting any more, so the second one states the fixed twelve
              months and the rule that makes them fair: the clock runs from the
              last visit, not from when the points were earned.
            */}
            <div className={styles.ruleNotes} id="tour-loyalty-rules">
              <div className={styles.ruleNote}>
                <Chip label="1." color="primary" />
                <p className={`body-medium ${styles.loyaltyDescription}`}>
                  {t('LOYALTY.DESCRIPTION')}
                </p>
              </div>
              <div className={styles.ruleNote}>
                <Chip label="2." color="primary" />
                <p className={`body-medium ${styles.loyaltyDescription}`}>
                  {t('LOYALTY.RULES.EXPIRY', {
                    count: POINTS_VALIDITY_MONTHS
                  })}
                </p>
              </div>
              <div className={styles.ruleNote}>
                <Chip label="3." color="primary" />
                <p className={`body-medium ${styles.loyaltyDescription}`}>
                  {/*
                    Reads the saved value, not the draft: these three sentences
                    describe the programme guests are in right now, and the
                    fields below are what it will become once saved.

                    Shown even when the bonus is off, because that is how the
                    owner learns the option exists — with a line that says how
                    to turn it on rather than one that states a rule.
                  */}
                  {rules.contactBonusPoints > 0
                    ? t('LOYALTY.RULES.CONTACT', {
                        count: rules.contactBonusPoints
                      })
                    : t('LOYALTY.RULES.CONTACT_OFF')}
                </p>
              </div>
            </div>

            <p
              className={`title-bold-small ${styles.sectionTitle}`}
              id="tour-loyalty-options"
            >
              {t('LOYALTY.OPTIONS.TITLE')}
            </p>
            <LoyaltyRules
              rules={draftRules}
              onChange={patch =>
                setDraftRules(current => ({ ...current, ...patch }))
              }
              disabled={!draftIsActive}
            />
            <CheckBox
              label={t('LOYALTY.PROGRAM_ACTIVE')}
              checked={draftIsActive}
              value={draftIsActive}
              onChange={setDraftIsActive}
            />
            <div className={styles.optionsSave} id="tour-loyalty-save">
              <Button
                label={t('LOYALTY.OPTIONS.SAVE')}
                color="primary"
                variant="contained"
                // Nothing to save is not a state worth offering.
                disabled={!hasUnsavedOptions}
                onClick={handleSaveOptions}
              />
            </div>
          </div>
        </Tile>
      </div>

      <div className={`${styles.loyaltyColumn} start-5 col-8`}>
        <Tile color="black">
          <div className={styles.loyaltySection}>
            <p className={`title-bold-medium ${styles.loyaltyTitle}`}>
              {t('LOYALTY.TITLE')}
            </p>

            <div className={styles.statsAnchor} id="tour-loyalty-stats">
              <LoyaltyStats stats={stats} />
            </div>

            {/*
              No heading and no add button of its own: the table's own toolbar
              carries the plus, wired to the same handler below.

              Last in the tile, so it takes whatever height the rest leaves.
            */}
            <div className={styles.tableWrapper} id="tour-loyalty-rewards">
              <Table
                // Re-keyed on the language so the column headers re-read t().
                key={`loyalty-rewards-${i18n.language}`}
                initialRows={rewardRows}
                columns={columns}
                columnsModel={columnsModel}
                tableKey={TABLE_KEYS.LOYALTY_REWARDS}
                filterModel={{ items: [] }}
                handleOnAddItem={handleAddReward}
                handleOnDeleteItems={setPendingDeleteIds}
              />
            </div>
          </div>
        </Tile>
      </div>

      <Modal
        title={
          editedRewardId
            ? t('LOYALTY.REWARDS.EDIT_TITLE')
            : t('LOYALTY.REWARDS.ADD_TITLE')
        }
        size="small"
        isOpen={isModalOpen}
        onClose={requestCloseModal}
      >
        {/*
          Plain prose, not an Alert: nothing has gone wrong and nothing needs
          acknowledging — it only says what the multi-select and the limit mean,
          which is the same whether a reward is being added or edited.
        */}
        <p className={`body-medium ${styles.rewardFormNote}`}>
          {/*
            One paragraph rather than a note per field, so the block keeps a
            single gutter above the form in a 400px modal that already carries
            three fields.
          */}
          {t('LOYALTY.REWARDS.FORM.DISHES_HINT')}{' '}
          {t('LOYALTY.REWARDS.FORM.LIMIT_HINT')}
        </p>
        <DynamicForm
          {...rewardFormConfig}
          superCondensed
          setIsDirty={setIsRewardFormDirty}
          FormActions={
            <div className={styles.rewardFormActions}>
              <Button
                label={t('COMMON.SUBMIT')}
                color="primary"
                variant="contained"
                type="submit"
              />
            </div>
          }
        />
      </Modal>
      {rewardDiscardConfirmation}

      {/*
        Deleting and hiding look identical to a guest, so the bin says which one
        this is before it happens — and says what survives it, because the log
        below keeps the reward's name after the reward is gone.
      */}
      <ConfirmationModal
        isOpen={pendingDeleteIds !== null}
        title={
          (pendingDeleteIds?.length ?? 0) > 1
            ? t('LOYALTY.REWARDS.DELETE_MANY_TITLE')
            : t('LOYALTY.REWARDS.DELETE_TITLE')
        }
        message={t('LOYALTY.REWARDS.DELETE_MESSAGE')}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDeleteIds(null)}
        // The reward form may be open behind this; clearing the global dirty
        // flag would stop it warning when it is closed unsaved.
        resetsFormDirty={false}
      />
    </Grid>
  )
}

export default Loyalty
